import { memo, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Box, Icon, Text } from "@repo/ui";
import type { Flashcard } from "../../memory-card/types/memoryApiTypes";
import type { MemoryCardUI } from "../../memory-card/models/MemoryCardUI";
import { MathJaxHtml } from "../../../../../components/mathJax";
import { flashcardFrontText } from "../../../../../components/memoryCardStack/flashcardStackUtils";
import { MemoryCardFlipSpringShell } from "../../../../../components/memoryCardStack/MemoryCardFlipSpringShell";
import stackStyles from "../../../../../components/memoryCardStack/AnimatedCardStack.module.scss";
import { useFlippableMemoryCardStyles } from "./FlippableMemoryCard.styles";

export type FlippableMemoryCardProps = {
  item: MemoryCardUI;
  index: number;
  cardCount: number;
  backgroundColor: string;
  isStackFront?: boolean;
  onBookmark?: () => void;
  onFlipToAnswer?: (item: Flashcard) => void;
};

function FlippableMemoryCardComponent({
  item,
  index,
  cardCount,
  backgroundColor,
  isStackFront = true,
  onBookmark,
  onFlipToAnswer,
}: FlippableMemoryCardProps) {
  const { t } = useTranslation("selflearn");
  const styles = useFlippableMemoryCardStyles();
  const [flipped, setFlipped] = useState(false);
  const [backMounted, setBackMounted] = useState(false);

  const cardId = item.flashcardId ?? item.memoryCardId ?? index;
  const isBookmarked = item.isBookmarked === true;

  useEffect(() => {
    setFlipped(false);
    setBackMounted(false);
  }, [cardId]);

  useEffect(() => {
    if (flipped) {
      setBackMounted(true);
    }
  }, [flipped]);

  const frontText = flashcardFrontText(item);
  const progressLabel = `${index + 1}/${cardCount}`;

  const backPlainFallback =
    item.description?.trim() ||
    item.summary?.trim() ||
    t("memoryCard.noAnswer", { defaultValue: "No answer available" });

  const pointerFront = !isStackFront ? "none" : flipped ? "none" : "auto";
  const pointerBack = !isStackFront ? "none" : flipped ? "auto" : "none";

  return (
    <div className={stackStyles.flipScene}>
      <MemoryCardFlipSpringShell flipped={flipped}>
        <div
          className={`${stackStyles.face} ${stackStyles.faceFront}`}
          style={{ backgroundColor, pointerEvents: pointerFront }}
        >
          <Box
            as="button"
            className={`${stackStyles.faceTapArea} ${stackStyles.faceContent}`}
            flexDirection="column"
            alignItems="stretch"
            flex={1}
            onClick={() => {
              if (!isStackFront) return;
              onFlipToAnswer?.(item);
              setFlipped(true);
            }}
          >
            <Box className={stackStyles.headerColumn} flexDirection="column">
              <Text as="span" className={stackStyles.labelText}>
                {t("memoryCard.question", { defaultValue: "Question" })}
              </Text>
              <Text as="span" className={stackStyles.progressText}>
                {progressLabel}
              </Text>
            </Box>
            <Box className={stackStyles.contentArea}>
              {item.front?.text?.trim() ? (
                <MathJaxHtml
                  className={stackStyles.mathWrap}
                  content={item.front.text}
                />
              ) : null}
              {item.front?.imageURL ? (
                <img
                  className={stackStyles.contentImage}
                  src={item.front.imageURL}
                  alt=""
                  decoding="async"
                  fetchPriority="high"
                />
              ) : null}
              {!item.front?.text?.trim() && !item.front?.imageURL ? (
                <Text
                  as="span"
                  className={`${stackStyles.contentText} ${stackStyles.contentTextQuestion}`}
                >
                  {frontText || "\u00A0"}
                </Text>
              ) : null}
            </Box>
            <Text
              as="span"
              className={stackStyles.tapHint}
              style={styles.accentText}
            >
              {t("memoryCard.tapToSeeAnswer", {
                defaultValue: "Tap to see answer",
              })}
            </Text>
          </Box>
        </div>

        <div
          className={`${stackStyles.face} ${stackStyles.faceBack}`}
          style={{ backgroundColor, pointerEvents: pointerBack }}
        >
          {isBookmarked ? (
            <Box className={stackStyles.bookmarkBadge} aria-hidden>
              <Icon
                name="bookmarkSl"
                width={104}
                height={104}
                color="var(--colors-neutral-100)"
              />
            </Box>
          ) : null}
          <Box className={stackStyles.backMain}>
            <Box
              as="button"
              className={`${stackStyles.faceTapArea} ${stackStyles.faceContent}`}
              flexDirection="column"
              alignItems="stretch"
              flex={1}
              onClick={() => {
                if (!isStackFront) return;
                setFlipped(false);
              }}
            >
              <Box
                className={`${stackStyles.headerColumn} ${stackStyles.headerColumnAnswer}`}
                flexDirection="column"
              >
                <Text as="span" className={stackStyles.labelText}>
                  {t("memoryCard.answer", { defaultValue: "Answer" })}
                </Text>
                <Text as="span" className={stackStyles.progressText}>
                  {progressLabel}
                </Text>
              </Box>
              <Box className={stackStyles.contentArea}>
                {backMounted && item.back?.text?.trim() ? (
                  <MathJaxHtml
                    className={stackStyles.mathWrap}
                    content={item.back.text}
                  />
                ) : !item.back?.imageURL ? (
                  <Text as="span" className={stackStyles.contentText}>
                    {backPlainFallback}
                  </Text>
                ) : null}
                {backMounted && item.back?.imageURL ? (
                  <img
                    className={stackStyles.contentImage}
                    src={item.back.imageURL}
                    alt=""
                    decoding="async"
                    loading="lazy"
                  />
                ) : null}
              </Box>
            </Box>
          </Box>
          <Box
            as="button"
            className={stackStyles.bookmarkBtn}
            style={styles.accentText}
            data-memory-no-fling
            onPointerDown={(event) => event.stopPropagation()}
            onClick={() => {
              if (!isStackFront) return;
              onBookmark?.();
            }}
            aria-label={t("memoryCard.bookmark", {
              defaultValue: "Bookmark",
            })}
          >
            <Icon
              name={isBookmarked ? "bookmarkSl" : "unBookmarkSl"}
              width={24}
              height={24}
              color="var(--colors-neutral-10)"
            />
            <Text as="span" className={stackStyles.bookmarkBtnText}>
              {t("memoryCard.bookmark", { defaultValue: "Bookmark" })}
            </Text>
          </Box>
        </div>
      </MemoryCardFlipSpringShell>
    </div>
  );
}

export const FlippableMemoryCard = memo(FlippableMemoryCardComponent);
